import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { Card, PageHeader, StatTile } from "../components/ui.js";

/**
 * A quick reference for the rules that come up mid-season. The full
 * Rules/Information document lives in the league's shared folder; this page
 * covers what you need at the point of picking teams or entering a result.
 */
export default function Rules() {
  const trpc = useTRPC();
  const rules = useQuery(trpc.league.rules.queryOptions());
  const season = useQuery(trpc.league.currentSeason.queryOptions());

  const r = rules.data;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Rules at a glance"
        subtitle={
          season.data?.rulesRevision
            ? `Current season runs on Rules/Information Rev ${season.data.rulesRevision}`
            : undefined
        }
      />

      {r && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            label="Regular season"
            value={`${r.quarterMinutesRegular} min`}
            hint="quarters"
          />
          <StatTile
            label="Playoffs"
            value={`${r.quarterMinutesPlayoff} min`}
            hint="quarters"
          />
          <StatTile
            label="Team use limit"
            value={r.teamUseLimitRegular}
            hint={`${r.teamUseLimitPlayoff} in the bracket`}
          />
          <StatTile label="OVR window" value={`+${r.ovrWindow}`} hint="on the 1st pick" />
        </div>
      )}

      <div className="space-y-6">
        <Card title="Picking teams">
          <div className="space-y-3 px-4 py-4 text-sm text-chalk-300">
            <p>
              The <strong className="text-chalk-50">home</strong> manager elects
              one of the following, and the away manager gets the other:
            </p>
            <ul className="ml-4 list-disc space-y-1 text-chalk-400">
              <li>Ban 1st and 4th, and pick your team second</li>
              <li>Ban 2nd and 3rd, and pick your team first</li>
            </ul>
            <p>
              The second team selected must be within{" "}
              <strong className="text-chalk-50">+2 OVR</strong> of the first, or
              lower. If bans and use limits leave no legal option, the second
              picker takes the next closest overall.
            </p>
            <p>
              Mirror matches aren't allowed. A manager may use the same roster{" "}
              <strong className="text-chalk-50">twice</strong> in a regular
              season and <strong className="text-chalk-50">once</strong> in the
              playoff bracket. Overall ratings drift over the real NFL season and
              the site doesn't record them, so the +2 window is settled between
              the two of you at the time.
            </p>
          </div>
        </Card>

        <Card title="Required game settings">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-4 text-sm sm:grid-cols-3">
            {[
              ["Time", "Any"],
              ["Weather", "Random (if outdoors)"],
              ["Accelerated clock", "On, 20 seconds"],
              ["Difficulty", "All-Pro"],
              ["Game style", "Simulation"],
              ["Even teams", "Off"],
              ["Injuries", "Off"],
              ["Fatigue", "On"],
              ["Commentary", "Tirico or Gaudin"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[11px] uppercase tracking-wider text-chalk-500">
                  {k}
                </dt>
                <dd className="text-chalk-200">{v}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card title="Scheduling and results">
          <div className="space-y-3 px-4 py-4 text-sm text-chalk-300">
            <p>
              Matches must be completed within their week. A week closes on the
              Sunday <em>after</em> next at 11:59pm ET, so there's always a full
              week for the next set of games.
            </p>
            <p>
              Pick a day and time with your opponent and post it in{" "}
              <strong className="text-chalk-50">#scheduling</strong>. Once the
              game is done you each file your own half here — your score, your
              quarters, your stat line — in whatever order you get to it. The
              result is final once both halves are in, and it's the same two
              entries the{" "}
              <strong className="text-chalk-50">#match-results</strong> channel
              sees.
            </p>
            <p>
              Play games out to the post-game stat screen. Tracking your stat
              line is strongly encouraged; the leaderboards and most of the
              league's articles depend on it.
            </p>
          </div>
        </Card>

        <Card title="Disconnects">
          <div className="space-y-2 px-4 py-4 text-sm text-chalk-300">
            <p>
              <strong className="text-chalk-50">First half:</strong> the game is
              replayed.
            </p>
            <p>
              <strong className="text-chalk-50">3rd quarter:</strong> if the
              leader is up by 22 or more they may accept the win or ask for a
              replay. Within 21, it's replayed.
            </p>
            <p>
              <strong className="text-chalk-50">4th quarter:</strong> if the
              leader is up by 9 or more they may accept the win or ask for a
              replay. Within 8, it's replayed.
            </p>
            <p className="text-chalk-400">
              Disconnecting on purpose for advantage is an automatic shutout
              loss on a first offence, and a ban on a second.
            </p>
          </div>
        </Card>

        <Card title="Tiebreakers">
          <ol className="space-y-1 px-4 py-4 text-sm text-chalk-300">
            {(r?.tiebreakers ?? []).map((t, i) => (
              <li key={t} className="flex gap-3">
                <span className="w-4 shrink-0 text-chalk-600">{i + 1}.</span>
                <span>{t}</span>
              </li>
            ))}
          </ol>
        </Card>

        <Card title="Questions and rule violations">
          <div className="space-y-3 px-4 py-4 text-sm text-chalk-300">
            <p>
              Ask the Co-Commissioners
              {r?.commissioners?.length ? ` (${r.commissioners.join(" and ")})` : ""}.
              Rule violation concerns go to the Co-Commissioners and may lead to
              an investigation; if it's upheld, the league discusses and votes on
              a penalty.
            </p>
            <p className="text-chalk-500">
              This page is a summary. The full Rules/Information document in the
              league folder is the authority.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
